import { BaseService } from './BaseService';
import { API_ENDPOINTS } from './config';
import { ArtifactItem, ArtifactItemPropertyValue } from '../../types/artifactItems';
import api from './api';

/**
 * Filters for items requiring user input
 */
export interface UserInputFilters {
  user_id?: string;
  artifact_id?: string;
  state_id?: string;
  company_id?: string;
  limit?: number;
  offset?: number;
}

/**
 * Filters for item queries
 */
export interface ItemFilters {
  artifact_id?: string;
  state_id?: string;
  company_id?: string;
  created_after?: string;
  created_before?: string;
  limit?: number;
  offset?: number;
}

/**
 * Update data for artifact item
 */
export interface ArtifactItemUpdate {
  name?: string;
  description?: string;
  item_data?: ArtifactItemPropertyValue;
  metadata?: Record<string, unknown>;
}

/**
 * Create item with state request
 */
export interface CreateItemWithState {
  artifact_id: string;
  initial_state_id: string;
  item_data: ArtifactItemPropertyValue;
  item_name?: string;
  description?: string;
}

/**
 * Current state response
 */
export interface ItemCurrentState {
  item_id: string;
  current_state_id: string;
  current_state_name?: string;
  state_type?: string;
  transitioned_at?: string;
}

/**
 * Service for artifact item operations
 */
export class ArtifactItemService extends BaseService {
  /**
   * Get artifact item by ID
   */
  static async getItemById(itemId: string): Promise<ArtifactItem | null> {
    try {
      return await this.get<ArtifactItem>(API_ENDPOINTS.ARTIFACT_ITEMS.BY_ID(itemId));
    } catch (error) {
      console.error('[ArtifactItemService] Error fetching item:', error);
      return null;
    }
  }

  /**
   * Update artifact item
   */
  static async updateItem(itemId: string, data: ArtifactItemUpdate): Promise<ArtifactItem> {
    return this.put<ArtifactItem>(
      API_ENDPOINTS.ARTIFACT_ITEMS.BY_ID(itemId),
      data as unknown as Record<string, unknown>
    );
  }

  /**
   * Get item properties
   * ISSUE-007: Enhanced property endpoint with metadata and global visibility filtering
   */
  static async getItemProperties(
    itemId: string,
    options?: {
      include_metadata?: boolean;
      include_global_visibility?: boolean;
    }
  ): Promise<ArtifactItemPropertyValue> {
    try {
      const params = new URLSearchParams();
      if (options?.include_metadata !== undefined) {
        params.append('include_metadata', options.include_metadata.toString());
      }
      if (options?.include_global_visibility !== undefined) {
        params.append(
          'include_global_visibility',
          options.include_global_visibility.toString()
        );
      }

      const endpoint =
        params.toString().length > 0
          ? `${API_ENDPOINTS.ARTIFACT_ITEMS.PROPERTIES(itemId)}?${params.toString()}`
          : API_ENDPOINTS.ARTIFACT_ITEMS.PROPERTIES(itemId);

      const response = await this.get<{ properties: ArtifactItemPropertyValue } | ArtifactItemPropertyValue>(
        endpoint
      );

      // Handle different response formats
      if (response && typeof response === 'object' && 'properties' in response) {
        return (response as { properties: ArtifactItemPropertyValue }).properties;
      }
      return response as ArtifactItemPropertyValue;
    } catch (error) {
      console.error('[ArtifactItemService] Error fetching item properties:', error);
      return {};
    }
  }

  /**
   * Get items requiring user input
   */
  static async getItemsRequiringUserInput(filters?: UserInputFilters): Promise<ArtifactItem[]> {
    try {
      const params = new URLSearchParams();
      if (filters?.user_id) params.append('user_id', filters.user_id);
      if (filters?.artifact_id) params.append('artifact_id', filters.artifact_id);
      if (filters?.state_id) params.append('state_id', filters.state_id);
      if (filters?.company_id) params.append('company_id', filters.company_id);
      if (filters?.limit) params.append('limit', filters.limit.toString());
      if (filters?.offset) params.append('offset', filters.offset.toString());

      const endpoint = params.toString()
        ? `${API_ENDPOINTS.ARTIFACT_ITEMS.REQUIRING_USER_INPUT}?${params.toString()}`
        : API_ENDPOINTS.ARTIFACT_ITEMS.REQUIRING_USER_INPUT;

      const response = await this.get<ArtifactItem[] | { items: ArtifactItem[] }>(endpoint);
      
      // Handle different response formats
      if (Array.isArray(response)) {
        return response;
      }
      if (response && typeof response === 'object' && 'items' in response) {
        return (response as { items: ArtifactItem[] }).items;
      }
      return [];
    } catch (error) {
      console.error('[ArtifactItemService] Error fetching items requiring user input:', error);
      return [];
    }
  }

  /**
   * Create item with initial state
   */
  static async createItemWithState(data: CreateItemWithState): Promise<ArtifactItem> {
    // The endpoint is just /artifact-items, the initial_state_id is in the request body
    return this.post<ArtifactItem>(
      API_ENDPOINTS.ARTIFACT_ITEMS.BASE,
      data as unknown as Record<string, unknown>
    );
  }

  /**
   * Get current state of an item
   */
  static async getItemCurrentState(itemId: string): Promise<ItemCurrentState | null> {
    try {
      return await this.get<ItemCurrentState>(API_ENDPOINTS.ARTIFACT_ITEMS.CURRENT_STATE(itemId));
    } catch (error) {
      console.error('[ArtifactItemService] Error fetching current state:', error);
      return null;
    }
  }

  /**
   * Get state history for an item
   */
  static async getItemStateHistory(itemId: string): Promise<any[]> {
    try {
      const response = await this.get<any[] | { history: any[] }>(
        API_ENDPOINTS.ARTIFACT_ITEMS.STATE_HISTORY(itemId)
      );
      
      // Handle different response formats
      if (Array.isArray(response)) {
        return response;
      }
      if (response && typeof response === 'object' && 'history' in response) {
        return (response as { history: any[] }).history;
      }
      return [];
    } catch (error) {
      console.error('[ArtifactItemService] Error fetching state history:', error);
      return [];
    }
  }

  /**
   * Get all items for an artifact
   */
  static async getItemsByArtifact(artifactId: string, filters?: ItemFilters): Promise<ArtifactItem[]> {
    try {
      const params = new URLSearchParams();
      if (filters?.state_id) params.append('state_id', filters.state_id);
      if (filters?.created_after) params.append('created_after', filters.created_after);
      if (filters?.created_before) params.append('created_before', filters.created_before);
      if (filters?.limit) params.append('limit', filters.limit.toString());
      if (filters?.offset) params.append('offset', filters.offset.toString());

      const endpoint = params.toString()
        ? `${API_ENDPOINTS.ARTIFACT_ITEMS.BY_ARTIFACT(artifactId)}?${params.toString()}`
        : API_ENDPOINTS.ARTIFACT_ITEMS.BY_ARTIFACT(artifactId);

      const response = await this.get<ArtifactItem[] | { items: ArtifactItem[] }>(endpoint);
      
      // Handle different response formats
      if (Array.isArray(response)) {
        return response;
      }
      if (response && typeof response === 'object' && 'items' in response) {
        return (response as { items: ArtifactItem[] }).items;
      }
      return [];
    } catch (error) {
      console.error('[ArtifactItemService] Error fetching items by artifact:', error);
      return [];
    }
  }

  /**
   * Get items in a specific state
   */
  static async getItemsByState(stateId: string, filters?: ItemFilters): Promise<ArtifactItem[]> {
    try {
      const params = new URLSearchParams();
      params.append('state_id', stateId);
      if (filters?.artifact_id) params.append('artifact_id', filters.artifact_id);
      if (filters?.company_id) params.append('company_id', filters.company_id);
      if (filters?.created_after) params.append('created_after', filters.created_after);
      if (filters?.created_before) params.append('created_before', filters.created_before);
      if (filters?.limit) params.append('limit', filters.limit.toString());
      if (filters?.offset) params.append('offset', filters.offset.toString());

      const endpoint = `${API_ENDPOINTS.ARTIFACT_ITEMS.BASE}?${params.toString()}`;

      const response = await this.get<ArtifactItem[] | { items: ArtifactItem[] }>(endpoint);
      
      // Handle different response formats
      if (Array.isArray(response)) {
        return response;
      }
      if (response && typeof response === 'object' && 'items' in response) {
        return (response as { items: ArtifactItem[] }).items;
      }
      return [];
    } catch (error) {
      console.error('[ArtifactItemService] Error fetching items by state:', error);
      return [];
    }
  }

  /**
   * Get items for a company
   */
  static async getItemsByCompany(companyId: string, filters?: ItemFilters): Promise<ArtifactItem[]> {
    try {
      const params = new URLSearchParams();
      params.append('company_id', companyId);
      if (filters?.artifact_id) params.append('artifact_id', filters.artifact_id);
      if (filters?.state_id) params.append('state_id', filters.state_id);
      if (filters?.created_after) params.append('created_after', filters.created_after);
      if (filters?.created_before) params.append('created_before', filters.created_before);
      if (filters?.limit) params.append('limit', filters.limit.toString());
      if (filters?.offset) params.append('offset', filters.offset.toString());

      const endpoint = `${API_ENDPOINTS.ARTIFACT_ITEMS.BASE}?${params.toString()}`;

      const response = await this.get<ArtifactItem[] | { items: ArtifactItem[] }>(endpoint);
      
      // Handle different response formats
      if (Array.isArray(response)) {
        return response;
      }
      if (response && typeof response === 'object' && 'items' in response) {
        return (response as { items: ArtifactItem[] }).items;
      }
      return [];
    } catch (error) {
      console.error('[ArtifactItemService] Error fetching items by company:', error);
      return [];
    }
  }
}

